import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import TeamActivityFeed from './TeamActivityFeed';
import SharedNotes from './SharedNotes';
import { VisibilityBadge } from './VisibilityIndicator';

const OrganizationDashboard = ({
  organization,
  privacyMode,
  currentUser,
  teamMembers = [],
  projects = [],
  activities = [],
  notes = [],
  onAddNote
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('week');
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  // Calculate team metrics
  const teamMetrics = {
    totalRevenue: projects.reduce((sum, p) => sum + (p.revenue || 0), 0),
    activeProjects: projects.filter(p => p.status === 'active').length,
    completedProjects: projects.filter(p => p.status === 'completed').length,
    teamUtilization: Math.round(
      (teamMembers.filter(m => m.hasActiveProject).length / teamMembers.length) * 100
    ),
    avgProjectDuration: Math.round(
      projects.reduce((sum, p) => sum + (p.duration || 0), 0) / projects.length
    ),
    clientSatisfaction: 4.8,
    onTimeDelivery: 92
  };

  // Pattern Recognition Insights
  const insights = [
    {
      type: 'warning',
      title: 'Payment Pattern Detected',
      description: 'Client ABC Corp typically pays 15 days late. Consider adjusting invoice terms.',
      icon: ExclamationTriangleIcon,
      color: 'from-amber-500 to-amber-600'
    },
    {
      type: 'success',
      title: 'High Performer',
      description: 'Sarah completed 40% more tasks than average this month.',
      icon: CheckCircleIcon,
      color: 'from-green-500 to-green-600'
    },
    {
      type: 'insight',
      title: 'Opportunity',
      description: 'Wedding season bookings are 30% higher. Consider focusing marketing efforts.',
      icon: LightBulbIcon,
      color: 'from-brand to-[#FF9D5C]'
    }
  ];

  // Team member cards
  const renderTeamMemberCard = (member) => (
    <motion.div
      key={member.id}
      className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-4 hover:border-brand transition-all duration-200"
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand to-[#FF9D5C] flex items-center justify-center">
          <span className="text-sm font-bold text-black">
            {member.initials}
          </span>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
            {member.name}
          </h4>
          <p className="text-xs text-gray-500 dark:text-[#666666]">
            {member.role}
          </p>
        </div>
        {member.isOnline && (
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {member.activeProjects || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-[#666666]">Projects</p>
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {member.completedTasks || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-[#666666]">Tasks</p>
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {member.utilization || 0}%
          </p>
          <p className="text-xs text-gray-500 dark:text-[#666666]">Utilized</p>
        </div>
      </div>

      {member.currentProject && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-[#2A2A2A]">
          <p className="text-xs text-gray-500 dark:text-[#666666]">
            Working on: <span className="text-brand">{member.currentProject}</span>
          </p>
        </div>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {organization?.name || 'Organization'} Dashboard
          </h1>
          <p className="text-gray-500 dark:text-[#666666]">
            Real-time insights across all projects and team members
          </p>
        </div>

        {/* Privacy Mode Notice */}
        {privacyMode === 'personal' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 px-4 py-3 bg-brand/10 border border-brand/30 rounded-xl"
          >
            <p className="text-sm text-brand">
              You're in Personal View. Showing only your data and items shared with you.
            </p>
          </motion.div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div
            className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <CurrencyDollarIcon className="w-8 h-8 text-green-500" />
              <span className="flex items-center gap-1 text-xs font-medium text-green-500">
                <ArrowTrendingUpIcon className="w-3 h-3" />
                12%
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              ${teamMetrics.totalRevenue.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 dark:text-[#666666] mt-1">
              Total Revenue
            </p>
          </motion.div>

          <motion.div
            className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <DocumentDuplicateIcon className="w-8 h-8 text-brand" />
              <span className="text-xs font-medium text-gray-500">
                {teamMetrics.completedProjects} completed
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {teamMetrics.activeProjects}
            </p>
            <p className="text-xs text-gray-500 dark:text-[#666666] mt-1">
              Active Projects
            </p>
          </motion.div>

          <motion.div
            className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <UsersIcon className="w-8 h-8 text-blue-500" />
              <span className="text-xs font-medium text-blue-500">
                {teamMetrics.teamUtilization}%
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {teamMembers.length}
            </p>
            <p className="text-xs text-gray-500 dark:text-[#666666] mt-1">
              Team Members
            </p>
          </motion.div>

          <motion.div
            className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <ClockIcon className="w-8 h-8 text-purple-500" />
              <span className="text-xs font-medium text-purple-500">
                {teamMetrics.onTimeDelivery}%
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {teamMetrics.avgProjectDuration}d
            </p>
            <p className="text-xs text-gray-500 dark:text-[#666666] mt-1">
              Avg Duration
            </p>
          </motion.div>
        </div>

        {/* Pattern Recognition Insights */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            AI Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {insights.map((insight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${insight.color}`}>
                    <insight.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {insight.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-[#666666]">
                      {insight.description}
                    </p>
                    <button className="mt-2 text-xs font-medium text-brand hover:text-brand-hover">
                      View Details →
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Activity Feed */}
          <div className="lg:col-span-2 space-y-6">
            <TeamActivityFeed
              activities={activities}
              privacyMode={privacyMode}
              currentUserId={currentUser?.id}
            />

            <SharedNotes
              projectId="organization"
              notes={notes}
              currentUser={currentUser}
              teamMembers={teamMembers}
              onAddNote={onAddNote}
              visibility="organization"
            />
          </div>

          {/* Right Column: Team & Projects */}
          <div className="space-y-6">
            {/* Team Members */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Team Members
              </h3>
              <div className="space-y-3">
                {teamMembers.slice(0, 3).map(renderTeamMemberCard)}
              </div>
              {teamMembers.length > 3 && (
                <button className="mt-3 w-full py-2 text-sm font-medium text-brand hover:text-brand-hover transition-colors">
                  View all {teamMembers.length} members →
                </button>
              )}
            </div>

            {/* Recent Projects */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Recent Projects
              </h3>
              <div className="space-y-3">
                {projects.slice(0, 5).map((project) => (
                  <motion.div
                    key={project.id}
                    className="flex items-center justify-between p-3 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl hover:border-brand transition-all duration-200"
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          {project.name}
                        </h4>
                        <VisibilityBadge visibility={project.visibility || 'organization'} />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-[#666666]">
                        {project.client} • {project.status}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        ${project.value?.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-[#666666]">
                        {project.progress}%
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDashboard;